import {useEffect, useState} from "react";
import {FormTodo, FormTodoType} from "../../components/form/FormTodo.tsx";
import {HiChevronRight} from "react-icons/hi";
import {SecondaryButton} from "../../components/general/SecondaryButton.tsx";
import {AddBusinessIcon} from "../../components/general/CustomIcons/AddBusinessIcon.tsx";

import {
    DEFAULT_FTE_AMOUNT,
    DEFAULT_LEGAL_ENTITY_TYPE,
    LEGAL_ENTITY_TYPE_OPTIONS,
} from "../../lib/businessRegistration.ts";

import {v4 as uuidv4} from 'uuid';
import type {LegalEntityType} from "../../generated/enums.ts";

export interface StepTwoFormData {
    id: string,
    business_name: string,
    registration_number: string,
    legal_entity_type: LegalEntityType,
    fte_amount: number,
    industry: string,
    business_description: string,
}

interface Props {
    onUpdateFunc: (formData: StepTwoFormData[]) => void
    formTodos: Array<FormTodoType>
    defaultBusinesses?: StepTwoFormData[]
}

const emptyBusiness: StepTwoFormData = {
    id: '',
    business_name: '',
    registration_number: '',
    legal_entity_type: DEFAULT_LEGAL_ENTITY_TYPE,
    fte_amount: DEFAULT_FTE_AMOUNT,
    industry: '',
    business_description: '',
}

function ensureBusinessId(business: StepTwoFormData): StepTwoFormData {
    if (business.id) {
        return business
    }
    return {...business, id: uuidv4()}
}

function createEmptyBusiness(): StepTwoFormData {
    return {...emptyBusiness, id: uuidv4()}
}

export function StepTwo({onUpdateFunc, formTodos, defaultBusinesses}: Props) {
    const [businesses, setBusinesses] = useState<StepTwoFormData[]>(
        (defaultBusinesses || []).map(ensureBusinessId)
    )
    const [expandedIndex, setExpandedIndex] = useState(0)

    useEffect(() => {
        onUpdateFunc(businesses)
    }, [businesses, onUpdateFunc])

    const updateBusiness = (index: number, updates: Partial<StepTwoFormData>) => {
        setBusinesses((prev) => prev.map((business, i) => i === index ? {...business, ...updates} : business))
    }

    const addBusiness = () => {
        setBusinesses((prev) => {
            return [...prev, createEmptyBusiness()]
        })
        setExpandedIndex(businesses.length)
    }
    const deleteBusiness = (index: number) => {
        setBusinesses((prev) => {
            return prev.filter((_, i) => i !== index)
        })

        setExpandedIndex(businesses.length - 1)
    }

    return (
        <div className="notranslate" translate="no">
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Vertel ons over je
                bedrijven</p>
            <p className="mt-4 text-base block text-gray-600 mb-12">
                Je kan meerdere entiteiten aan PocketCFO koppelen. Bijvoorbeeld je werk-BV en je holding. Of meerdere
                entiteiten in je groep. We gebruiken deze input om het product op maat voor je te maken
            </p>
            <form action="#" method="POST" className="space-y-5">
                {businesses?.map((business, index) => {
                    const businessLabel = business.business_name?.trim() || `Bedrijf ${index + 1}`
                    const businessHasTodo = formTodos.some((todo) => todo.fieldName.startsWith(`businesses[${index}]`))
                    const isExpanded = expandedIndex === index || businessHasTodo
                    return (
                        <div key={business.id}
                             className={`space-y-4 bg-gray-50 p-6 rounded-xl ${!isExpanded ? 'hover:bg-gray-100 cursor-pointer' : ''}`}>
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    className="flex-1 flex items-center justify-between text-left text-lg font-semibold text-gray-900"
                                    onClick={() => setExpandedIndex(isExpanded ? -1 : index)}
                                >
                                    <span>{businessLabel}</span>
                                    <span
                                        className={`inline-flex items-center justify-center rounded-md bg-gray-100 p-1.5 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                                    <HiChevronRight className="h-4 w-4"/>
                                </span>
                                </button>
                                <SecondaryButton
                                    title="Verwijder"
                                    ariaLabel={`Verwijder ${businessLabel}`}
                                    className="text-red-700 ring-red-200 hover:bg-red-50"
                                    onClick={() => deleteBusiness(index)}
                                />
                            </div>
                            <div
                                className={`space-y-6 overflow-hidden transition-[max-height,opacity] duration-200 ease-out ${isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 h-0 opacity-0'}`}
                                aria-hidden={!isExpanded}
                            >
                                <div className="flex w-full gap-6">
                                    <div className="grow">
                                        <label htmlFor={`business_name_${index}`}
                                               className="block text-sm font-medium leading-6 text-gray-900">
                                            Bedrijfsnaam
                                        </label>
                                        <div className="mt-2">
                                            <input
                                                id={`business_name_${index}`}
                                                name="business_name"
                                                type="text"
                                                required
                                                value={business.business_name}
                                                onChange={(e) => updateBusiness(index, {business_name: e.target.value})}
                                                autoComplete="organization"
                                                className="px-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                            />
                                            <FormTodo fieldName={`businesses[${index}].business_name`}
                                                      formTodos={formTodos}/>

                                        </div>
                                    </div>
                                    <div className="grow">
                                        <label htmlFor={`registration_number_${index}`}
                                               className="block text-sm font-medium leading-6 text-gray-900">
                                            KVK-nummer
                                        </label>
                                        <div className="mt-2">
                                            <input
                                                id={`registration_number_${index}`}
                                                name="registration_number"
                                                type="text"
                                                inputMode="numeric"
                                                pattern="\\d{8}"
                                                required
                                                value={business.registration_number}
                                                onChange={(e) => updateBusiness(index, {registration_number: e.target.value})}
                                                className="px-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                            />
                                            <FormTodo fieldName={`businesses[${index}].registration_number`}
                                                      formTodos={formTodos}/>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor={`legal_entity_type_${index}`}
                                           className="block text-sm font-medium leading-6 text-gray-900">
                                        Rechtsvorm
                                    </label>
                                    <select
                                        id={`legal_entity_type_${index}`}
                                        name="legal_entity_type"
                                        value={business.legal_entity_type}
                                        onChange={(e) => updateBusiness(index, {legal_entity_type: e.target.value as LegalEntityType})}
                                        className="mt-2 px-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-15 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-primary sm:text-sm sm:leading-6"
                                    >
                                        {LEGAL_ENTITY_TYPE_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    <FormTodo fieldName={`businesses[${index}].legal_entity_type`}
                                              formTodos={formTodos}/>

                                </div>

                                <div>
                                    <label htmlFor={`fte_amount_${index}`}
                                           className="block text-sm font-medium leading-6 text-gray-900">
                                        Hoeveel mensen zitten er in je team?
                                    </label>
                                    <div className="mt-2">
                                        <input
                                            id={`fte_amount_${index}`}
                                            name="fte_amount"
                                            min={1}
                                            type="number"
                                            value={business.fte_amount}
                                            onChange={(e) => updateBusiness(index, {fte_amount: parseInt(e.target.value)})}
                                            required
                                            className="px-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                        />
                                        <FormTodo fieldName={`businesses[${index}].fte_amount`} formTodos={formTodos}/>

                                    </div>
                                </div>

                                <div className="grow">
                                    <label htmlFor={`industry_${index}`}
                                           className="block text-sm font-medium leading-6 text-gray-900">
                                        Branche
                                    </label>
                                    <div className="mt-2">
                                        <input
                                            id={`industry_${index}`}
                                            name="industry"
                                            type="text"
                                            required
                                            value={business.industry}
                                            onChange={(e) => updateBusiness(index, {industry: e.target.value})}
                                            autoComplete="off"
                                            className="px-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                        />
                                        <FormTodo fieldName={`businesses[${index}].industry`} formTodos={formTodos}/>

                                    </div>
                                </div>

                                <div>
                                    <label htmlFor={`business_description_${index}`}
                                           className="block text-sm font-medium leading-6 text-gray-900">
                                        Vertel in een paar woorden over je bedrijf. Wie zijn je klanten? Wat verkoop je?
                                    </label>
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-500 mb-2">We maken gebruik van deze data om nog
                                            meer relevante inzichten en alerts naar je te sturen. De data blijft veilig
                                            en binnen je omgeving, en wordt gebruikt door ons systeem om betere context
                                            van je bedrijf te hebben.</p>
                                        <textarea
                                            id={`business_description_${index}`}
                                            name="business_description"
                                            rows={4}
                                            onChange={(e) => updateBusiness(index, {business_description: e.target.value})}
                                            className="px-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                                            value={business.business_description}
                                        />
                                        <FormTodo fieldName={`businesses[${index}].business_description`}
                                                  formTodos={formTodos}/>

                                    </div>
                                </div>
                            </div>
                        </div>
                    )
                })}
                <div className="flex flex-col items-center gap-3 w-full py-12">
                    <button
                        className={'flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100 transform p-8 rounded-xl gap-2'}
                        onClick={(e) => {
                            e.preventDefault()
                            addBusiness()
                        }}>
                        <AddBusinessIcon/>
                        <p className={'text-gray-500'}>Voeg een {businesses.length ? 'ander' : ''} bedrijf toe</p>
                    </button>
                </div>
            </form>
        </div>
    )
}
